import './ReviewerTimeOnlineContainer.scss';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import utcPlugin from 'dayjs/plugin/utc';
import {
  ColumnConfig,
  Reports,
  RightPanelConfig,
} from '../../config/reportTypes';
import StandardReports from '../../StandardReports';
import {
  useExportReviewerCounts,
  useGetReportState,
  useGetReviewerTimeOnline,
  useGetReviewerTimeOnlineView,
  useLeftPanel,
  useLeftPanelReport,
} from '../../../../../hooks/reporting/useReporting';
import {
  createDynamicColumns,
  createGridRows,
  getReviewerTimeOnlieColumns,
  getSelectedReviewer,
  getTimeOnlieColumn,
  mergeApiDataIntoConfig,
  ReviewerTimeOnlineReportType,
} from './utils';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrWaitToCompleteModalWrapper from '../../component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import dayjs from 'dayjs';
import {t} from 'i18next';
import {
  PAGE_SIZE,
  REPORT_PAGE_SIZE_OPTIONS,
  ToastType,
} from '../../../../../shared/constants';
import clsx from 'clsx';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import jsonReportConfig from '../../config/ReviewerTimeOnline.json';
import {ToastMessageConfigType} from '../../../../../api/types/ingestionApi.type';

dayjs.extend(utcPlugin);

type SavedFilterValues = Record<string, any>;

type LocationState = {
  item?: {label?: string};
  reportId?: number | string;
  isBackgroundReport?: boolean;
};

const ReviewerTimeOnlineContainer: React.FC = () => {
  const [reportConfig] = useState<Reports>(jsonReportConfig as Reports);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(PAGE_SIZE);

  const [savedFilterValues, setSavedFilterValues] = useState<SavedFilterValues>(
    {}
  );

  const hiddenColumnsRef = useRef<number[]>([]);
  const resetRef = useRef<boolean>(false);
  const [toastConfig, setToastConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({show: false});
  const [dataAsOf, setDataAsOf] = useState<string>(() =>
    dayjs.utc().format('YYYY/MM/DD HH:mm:ss')
  );

  const location = useLocation();
  const {item, reportId, isBackgroundReport} = (location.state ||
    {}) as LocationState;

  const {data: leftPanelData, isPending: isLoadingLeftPanel} = useLeftPanel(5);

  const {data: leftPanelReport} = useLeftPanelReport(reportId);
  const [isQueryRun, setIsQueryRun] = useState(false);

  const {
    isLoading: isReportStateLoading,
    data: savedReportStateData,
    error: reportStateError,
  } = useGetReportState(reportId, isBackgroundReport);

  const {
    mutate: getReviewerTimeOnline,
    data: reviewerTimeOnlineData,
    isPending: isLoadingReviewerTimeOnline,
    reset: resetReviewerTimeOnlineData,
  } = useGetReviewerTimeOnline();

  const {
    mutate: getReviewerTimeOnlineView,
    data: reviewerTimeOnlineViewData,
    isPending: isLoadingGridData,
    reset: resetReviewerTimeOnlineViewData,
  } = useGetReviewerTimeOnlineView(
    () => {
      setIsQueryRun(true);
    },
    () => {
      setIsQueryRun(false);
    }
  );

  const {mutate: exportReviewerCounts} = useExportReviewerCounts();

  useEffect(() => {
    if (reportStateError) {
      setToastConfig({
        show: true,
        type: ToastType.Error,
        message:
          (reportStateError as any)?.response?.data?.title ||
          t('reports.common.reportAPIError'),
      });
    }
  }, [reportStateError]);

  useEffect(() => {
    if (leftPanelReport && leftPanelData) {
      const getSelectedFilters: SavedFilterValues = {
        selectedReviewers: leftPanelData.reviewerList.map((it: any) =>
          leftPanelReport.selectedReviewers.includes(it.reviewerID)
        ),
        selectedAssignments: leftPanelReport.selectedAssignments.map((v: any) =>
          v.toString()
        ),
        fromDateTime: leftPanelReport.fromDateTime,
        toDateTime: leftPanelReport.toDateTime,
        timeOffset: leftPanelReport.timeOffset,
      };
      setSavedFilterValues(getSelectedFilters);
    }
  }, [leftPanelData, leftPanelReport]);

  const exportReport = useCallback(() => {
    const payload = {
      selectionCriteria: {
        selectedAssignments: savedFilterValues?.selectedAssignments,
        selectedReviewers: getSelectedReviewer(
          savedFilterValues,
          leftPanelData
        ),
        fromDateTime: savedFilterValues?.fromDateTime,
        toDateTime: savedFilterValues?.toDateTime,
        timeOffset: savedFilterValues?.timeOffset,
        reportTypeCode: ReviewerTimeOnlineReportType,
        assignmentFlag: true,
        selectedCustomColumns: hiddenColumnsRef.current,
        dataAsOf: dayjs
          .utc(dataAsOf, 'YYYY/MM/DD HH:mm:ss', true)
          .toISOString(),
      },
      actionType: 'EXPORTREPORT',
      reportType: ReviewerTimeOnlineReportType,
    };

    exportReviewerCounts(payload as any, {
      onSuccess: () => {
        setToastConfig({
          show: true,
          type: ToastType.Success,
          message: t('reviewerTimeOnline.exportSuccessToast'),
        });
      },
    });
  }, [savedFilterValues, leftPanelData, exportReviewerCounts, dataAsOf]);

  const reviewerTimeOnlineGridData = useMemo(() => {
    if (reviewerTimeOnlineViewData) return reviewerTimeOnlineViewData;

    if (savedReportStateData)
      return {
        reportData: (savedReportStateData as any).reviewerTimeOnlineReportData,
      };
    return undefined;
  }, [reviewerTimeOnlineViewData, savedReportStateData]);

  const rightPanelHeaderConfig: RightPanelConfig = useMemo(() => {
    return {
      title: t('reviewerTimeOnline.title'),
      subTitle: reviewerTimeOnlineGridData ? `Data as of ${dataAsOf} UTC` : '',
      showUploadIcon: true,
      showSaveIcon: true,
      showColumnsIcon: true,
      showActionButton: false,
      onUploadClick: exportReport,
    };
  }, [exportReport, reviewerTimeOnlineGridData, dataAsOf]);

  const columns = useMemo<ColumnConfig[]>(
    () => {
      const dateHeaderList =
        reviewerTimeOnlineGridData?.reportData?.dateHeaderList || [];
      const getColumns: ColumnConfig[] = [
        ...getReviewerTimeOnlieColumns(),
        ...createDynamicColumns(dateHeaderList, savedFilterValues),
        ...getTimeOnlieColumn(),
      ];
      return getColumns;
    },
    [
      reviewerTimeOnlineGridData?.reportData?.dateHeaderList,
      savedFilterValues,
    ] as any
  );

  const rows = useMemo(() => {
    const reportDataList =
      reviewerTimeOnlineGridData?.reportData?.reportDataList || [];
    const totalTimeOfDay =
      reviewerTimeOnlineGridData?.reportData?.totalTimeOfDay || [];
    const totalTime = reviewerTimeOnlineGridData?.reportData?.totalTime || 0;
    const dateHeaderLen =
      (reviewerTimeOnlineGridData?.reportData?.dateHeaderList || []).length ||
      0;

    return createGridRows(
      reportDataList,
      totalTimeOfDay,
      totalTime,
      dateHeaderLen
    );
  }, [
    reviewerTimeOnlineGridData?.reportData?.dateHeaderList?.length,
    reviewerTimeOnlineGridData?.reportData?.reportDataList,
    reviewerTimeOnlineGridData?.reportData?.totalTime,
    reviewerTimeOnlineGridData?.reportData?.totalTimeOfDay,
  ]);

  const saveReportConfigPayload = useMemo(() => {
    if (reviewerTimeOnlineData && savedFilterValues) {
      return {
        reportId: reviewerTimeOnlineData.reportData.reportID,
        selectedAssignments: savedFilterValues.selectedAssignments,
        selectedReviewers: getSelectedReviewer(
          savedFilterValues,
          leftPanelData
        ),
        fromDateTime: savedFilterValues.fromDateTime,
        toDateTime: savedFilterValues.toDateTime,
        timeOffset: savedFilterValues.timeOffset,
        reportTypeCode: ReviewerTimeOnlineReportType,
        assignmentFlag: true,
        selectedCustomColumns: hiddenColumnsRef.current,
      };
    }

    return undefined;
  }, [reviewerTimeOnlineData, savedFilterValues, leftPanelData]);

  const getGridData = useCallback(() => {
    if (reviewerTimeOnlineData) {
      getReviewerTimeOnlineView({
        reportID: reviewerTimeOnlineData.reportData.reportID,
        reportFilePath: reviewerTimeOnlineData.reportData.reportFilePath,
        reportName: reviewerTimeOnlineData.reportData.reportName,
        inputflag: reviewerTimeOnlineData.reportData.inputflag,
      });
    }
  }, [getReviewerTimeOnlineView, reviewerTimeOnlineData]);

  const handlePageNumber = useCallback((pageNumber: number) => {
    setPageNumber(pageNumber);
  }, []);

  const handleItemsPerPage = useCallback((itemsPerPage: number) => {
    setItemsPerPage(itemsPerPage);
    setPageNumber(1);
  }, []);

  if (
    !reportConfig ||
    (reportId && !leftPanelReport) ||
    !leftPanelData ||
    isReportStateLoading
  ) {
    return <ArrLoadingSpinner isOpen overlay />;
  }

  const getReportContent = () => {
    if (resetRef.current) return undefined;
    return leftPanelReport;
  };

  const updatedConfig = leftPanelData
    ? mergeApiDataIntoConfig(
        reportConfig,
        leftPanelData,
        savedFilterValues,
        getReportContent()
      )
    : reportConfig;

  const handleColumnToggleAction = async (columnId: string) => {
    const hiddenColumns = columns.reduce((indexes: number[], col, idx) => {
      if (col.field === columnId && hiddenColumnsRef.current.includes(idx)) {
        hiddenColumnsRef.current = hiddenColumnsRef.current.filter(
          (value) => value !== idx
        );
      } else if (
        col.field === columnId &&
        !hiddenColumnsRef.current.includes(idx)
      ) {
        indexes.push(idx);
      }
      return indexes;
    }, []);
    hiddenColumnsRef.current = [
      ...hiddenColumnsRef.current,
      ...hiddenColumns,
    ].sort((a, b) => a - b);
  };

  const handleActionClick = async (
    actionKey: string,
    values: Record<string, any>
  ) => {
    hiddenColumnsRef.current = [];
    if (actionKey === 'applyChanges') {
      setDataAsOf(dayjs.utc().format('YYYY/MM/DD HH:mm:ss'));
      setSavedFilterValues(values);
      getReviewerTimeOnline({
        selectedAssignments: values.selectedAssignments ?? [],
        selectedReviewers: getSelectedReviewer(values, leftPanelData),
        fromDateTime: values.fromDateTime,
        toDateTime: values.toDateTime,
        timeOffset: values.timeOffset,
        reportTypeCode: ReviewerTimeOnlineReportType,
        assignmentFlag: (values.selectedAssignments?.length ?? 0) > 0,
        selectedCustomColumns: [],
      });
    } else if (actionKey === 'reset') {
      resetRef.current = true;
      setSavedFilterValues({});
      setPageNumber(1);
      setIsQueryRun(false);
      resetReviewerTimeOnlineData();
      resetReviewerTimeOnlineViewData();
    }
  };

  return (
    <>
      <StandardReports
        className="reviewer-time-online-container"
        rightPanelHeaderConfig={rightPanelHeaderConfig}
        title={reportId && item?.label ? item?.label : reportConfig.title}
        showInfoIcon={reportConfig.showInfoIcon}
        infoContent={reportConfig.infoContent}
        infoModalTitle={reportConfig.infoModalTitle}
        reportsConfig={updatedConfig.config}
        loading={
          isLoadingLeftPanel || isLoadingGridData || isLoadingReviewerTimeOnline
        }
        columns={columns}
        data={rows}
        getRowId={(row) => row.id}
        mapRows={(data) => data}
        selectedFilterValues={
          Object.keys(savedFilterValues).length ? savedFilterValues : undefined
        }
        selectedOption={null}
        gridConfig={{
          className: clsx('reviewer-time-online-grid'),
          primaryKey: 'id',
          totalRecords: rows.length,
          disableVirtualization: false,
          pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
          itemsPerPage,
          currentPage: pageNumber,
          paginationMode: 'client',
          sortingMode: 'client',
          hideFooter: rows.length <= PAGE_SIZE,
          getRowClassName: (params) =>
            params.row.id === t('reviewerTimeOnline.total')
              ? 'total-cell-container'
              : '',
          getRowHeight: () => 'auto',
          setItemsPerPage: handleItemsPerPage,
          onPageChange: handlePageNumber,
        }}
        saveReportConfig={saveReportConfigPayload}
        savedData={leftPanelReport}
        enableSave={isQueryRun}
        onAction={handleActionClick}
        onColumnToggleAction={handleColumnToggleAction}
      />
      {reviewerTimeOnlineData?.reportData?.reportID &&
        reviewerTimeOnlineData?.reportData?.notificationID && (
          <ArrWaitToCompleteModalWrapper
            showClose={false}
            reportId={reviewerTimeOnlineData.reportData.reportID}
            notificationId={reviewerTimeOnlineData.reportData.notificationID}
            onComplete={getGridData}
          />
        )}
      {toastConfig.show && toastConfig.message && toastConfig.type && (
        <ArrToastNotification
          message={toastConfig.message}
          status={[toastConfig.type]}
          onClose={() => setToastConfig({show: false})}
        />
      )}
    </>
  );
};

export {ReviewerTimeOnlineContainer};
